// @flow

import React, { useState } from "react";
import type { SelectOption } from "../../components/Select/Select";

import EditableField from "../../components/EditableField";
import { denormalizeJsonApi } from "../../utils/denormalizeJsonApi";
import styles from "./Details.scss";

import { type BankActions } from "./Details";
import { EXCLUDED_BANKS } from "./formFields";

type BankType = {
  bank: {
    code: string,
    id: string,
    name: string
  },
  id: string,
  contact?: {
    firstName: string,
    lastName: string
  }
};

type FormattedBankType = {
  id: string,
  name: string,
  code: string,
  bank: {
    id: string
  }
};

type BankAsyncSelectProps = {
  bankActions: BankActions,
  banksData: {
    usedBanks: BankType[],
    excludedBanks: BankType[],
    bankSuggestions: BankType[]
  },
  onError: number => void
};

const getBankOptions: (FormattedBankType[]) => SelectOption[] = banks =>
  banks.map(bank => ({
    label: `${bank.name} - ${bank.code}`,
    labelOnSelected: bank.name,
    value: bank.id
  }));

const listMapper = (bankArray: BankType[]) =>
  bankArray.length ? (
    <ol className={styles.bankList}>
      {bankArray.map(({ id, bank: { name } }) => (
        <li key={id}>{name}</li>
      ))}
    </ol>
  ) : (
    "-"
  );

const formatExcludedBanks = (banks: BankType[]) =>
  banks.map(({ id, bank }) => ({
    id: bank.id,
    name: bank.name,
    code: bank.code,
    excludedBankId: id
  }));

const BankAsyncSelect = ({ bankActions, banksData, onError }: BankAsyncSelectProps) => {
  const { getBanks } = bankActions;

  const [bankOptions, updateOptions] = useState({
    bank: getBankOptions(formatExcludedBanks(banksData.excludedBanks))
  });

  const getOptions = (text: string) => {
    const shouldFetch = Array.isArray(bankOptions[text]);
    if (!shouldFetch) {
      const idsToExclude = [...banksData.bankSuggestions, ...banksData.usedBanks].map(
        ({ bank: { id } }) => id
      );

      getBanks(text)
        .then(data => {
          const options = denormalizeJsonApi(data);
          if (!options) {
            return;
          }

          const filteredOptions = options.filter(({ id }) => !idsToExclude.includes(id));
          const formattedExcludedBanks = formatExcludedBanks(banksData.excludedBanks);

          updateOptions(state => ({
            ...state,
            [text]: getBankOptions([...filteredOptions, ...formattedExcludedBanks])
          }));
        })
        .catch(err => {
          onError(err.response && err.response.status);
        });
    }
  };

  return (
    <EditableField
      fieldControlProps={{
        name: EXCLUDED_BANKS,
        caption: () => "Ausgeschlossene Banken"
      }}
      type="tagList"
      displayValue={() => listMapper(banksData.excludedBanks)}
      inputProps={{
        displayTag: option => option.label,
        placeholder: "Bankleitzahl oder Bankname suchen...",
        options: bankOptions,

        getAsyncOptions: text => getOptions(text)
      }}
    />
  );
};

export default BankAsyncSelect;
